import { useState, useEffect, useRef, useCallback } from "react";
import { DateRange, Calendar } from "react-date-range";
import {
  format,
  subDays,
  addDays,
  startOfMonth,
  endOfMonth,
  isSameDay,
} from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { MdKeyboardArrowDown } from "react-icons/md";

const getLast30DaysRange = () => {
  const today = new Date();
  const endDate = subDays(today, 1);
  const startDate = subDays(endDate, 29);
  return { startDate, endDate };
};

const getCurrentMonthRange = () => {
  const today = new Date();
  return {
    startDate: startOfMonth(today),
    endDate: endOfMonth(today),
  };
};

const getTodayRange = () => {
  const today = new Date();
  return {
    startDate: today,
    endDate: today,
  };
};

const today = new Date();

const presets = [
  {
    label: "Yesterday",
    range: () => ({
      startDate: subDays(new Date(), 1),
      endDate: subDays(new Date(), 1),
    }),
  },
  {
    label: "Last 7 Days",
    range: () => ({
      startDate: subDays(new Date(), 6),
      endDate: new Date(),
    }),
  },
  {
    label: "Last 15 Days",
    range: () => ({
      startDate: subDays(new Date(), 14),
      endDate: new Date(),
    }),
  },
  {
    label: "Last 30 Days",
    range: () => {
      const { startDate, endDate } = getLast30DaysRange();
      return { startDate, endDate };
    },
  },
  {
    label: "Last Month",
    range: () => {
      const now = new Date();
      const firstDay = startOfMonth(subDays(now, now.getDate()));
      const lastDay = endOfMonth(subDays(now, now.getDate()));
      return { startDate: firstDay, endDate: lastDay };
    },
  },
  {
    label: "Tomorrow",
    range: () => ({
      startDate: addDays(new Date(), 1),
      endDate: addDays(new Date(), 1),
    }),
  },
  {
    label: "Next 7 Days",
    range: () => ({
      startDate: new Date(),
      endDate: addDays(new Date(), 6),
    }),
  },
  {
    label: "Next 15 Days",
    range: () => ({
      startDate: new Date(),
      endDate: addDays(new Date(), 14),
    }),
  },
  {
    label: "Next 30 Days",
    range: () => ({
      startDate: new Date(),
      endDate: addDays(new Date(), 29),
    }),
  },
  {
    label: "Next Month",
    range: () => {
      const now = new Date();
      const firstDay = startOfMonth(addDays(endOfMonth(now), 1));
      const lastDay = endOfMonth(addDays(endOfMonth(now), 1));
      return { startDate: firstDay, endDate: lastDay };
    },
  },
];

const DateRangePickerStyle = ({
  onDateChange,
  initialRange,
  className = "h-[30px]",
  padding_css = "py-1",
  wrapperWidth = "w-[200px]",
  mode = "range",
  position_box = "right-0",
  disableInitialDateCall = false,
  defaultRangeType = "all",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [selectDate, setSelectDate] = useState(null);
  const pickerRef = useRef();

  const [range, setRange] = useState([
    { startDate: today, endDate: today, key: "selection" },
  ]);

  const [singleDate, setSingleDate] = useState(today);

  const previousRangeRef = useRef({
    startDate: today,
    endDate: today,
  });

  const isInitialMountRef = useRef(true);
  const [position, setPosition] = useState("left-0");

  useEffect(() => {
    if (mode === "range") {
      if (initialRange?.startDate && initialRange?.endDate) {
        const start = new Date(initialRange.startDate);
        const end = new Date(initialRange.endDate);

        setRange([
          {
            startDate: start,
            endDate: end,
            key: "selection",
          },
        ]);

        setSelectDate({
          startDate: format(start, "yyyy-MM-dd"),
          endDate: format(end, "yyyy-MM-dd"),
        });

        previousRangeRef.current = {
          startDate: start,
          endDate: end,
        };

        if (onDateChange && !disableInitialDateCall) {
          const formatted = {
            startDate: format(start, "yyyy-MM-dd"),
            endDate: format(end, "yyyy-MM-dd"),
          };

          onDateChange(formatted);
        }
      } else {
        if (defaultRangeType === "all") {
          setSelectDate(null);

          setRange([
            {
              startDate: today,
              endDate: today,
              key: "selection",
            },
          ]);

          previousRangeRef.current = {
            startDate: today,
            endDate: today,
          };

          isInitialMountRef.current = false;

          return;
        }

        let defaultStartDate;
        let defaultEndDate;

        switch (defaultRangeType) {
          case "last30days":
            const last30Days = getLast30DaysRange();
            defaultStartDate = last30Days.startDate;
            defaultEndDate = last30Days.endDate;
            break;

          case "currentmonth":
            const currentMonth = getCurrentMonthRange();
            defaultStartDate = currentMonth.startDate;
            defaultEndDate = currentMonth.endDate;
            break;

          case "today":
          default:
            const todayRange = getTodayRange();
            defaultStartDate = todayRange.startDate;
            defaultEndDate = todayRange.endDate;
            break;
        }

        const formatted = {
          startDate: format(defaultStartDate, "yyyy-MM-dd"),
          endDate: format(defaultEndDate, "yyyy-MM-dd"),
        };

        setRange([
          {
            startDate: defaultStartDate,
            endDate: defaultEndDate,
            key: "selection",
          },
        ]);

        setSelectDate(formatted);

        previousRangeRef.current = {
          startDate: defaultStartDate,
          endDate: defaultEndDate,
        };

        if (onDateChange && !disableInitialDateCall) {
          onDateChange(formatted);
        }
      }
    } else if (mode === "single") {
      if (initialRange?.startDate) {
        const date = new Date(initialRange.startDate);

        setSingleDate(date);

        setSelectDate({
          date: format(date, "yyyy-MM-dd"),
        });

        if (onDateChange && !disableInitialDateCall) {
          onDateChange({
            date: format(date, "yyyy-MM-dd"),
          });
        }
      } else {
        const formatted = {
          date: format(today, "yyyy-MM-dd"),
        };

        setSelectDate(formatted);

        if (onDateChange && !disableInitialDateCall) {
          onDateChange(formatted);
        }
      }
    }

    isInitialMountRef.current = false;
  }, [
    initialRange,
    mode,
    defaultRangeType,
    onDateChange,
    disableInitialDateCall,
  ]);

  useEffect(() => {
    if (isOpen && pickerRef.current) {
      const containerRect =
        pickerRef.current.getBoundingClientRect();

      const viewportWidth = window.innerWidth;

      if (
        containerRect.right + 600 >
        viewportWidth
      ) {
        setPosition("right-0");
      } else {
        setPosition("left-0");
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
  }, []);

  const handleSingleDateChange = (date) => {
    setSingleDate(date);
    setIsOpen(false);
  };

  const handleSelectAllRecords = () => {
    setSelectDate(null);

    setRange([
      {
        startDate: today,
        endDate: today,
        key: "selection",
      },
    ]);

    previousRangeRef.current = {
      startDate: today,
      endDate: today,
    };

    onDateChange?.({});

    setShowOptionsMenu(false);
    setIsOpen(false);
  };

  const handleSelectDateOption = () => {
    setShowOptionsMenu(false);
    setIsOpen(true);
  };

  const applyPreset = useCallback(
    (preset) => {
      const { startDate, endDate } = preset.range();

      setRange([
        {
          startDate,
          endDate,
          key: "selection",
        },
      ]);

      const formatted = {
        startDate: format(startDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
      };

      setSelectDate(formatted);
      onDateChange?.(formatted);

      previousRangeRef.current = {
        startDate,
        endDate,
      };

      setIsOpen(false);
    },
    [onDateChange]
  );

  const hasCustomDate =
    mode === "range" &&
    selectDate?.startDate &&
    selectDate?.endDate;

  return (
    <div
      className="relative"
      ref={pickerRef}
    >
      {mode === "range" ? (
        <>
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setShowOptionsMenu((prev) => !prev);
            }}
            className={`${wrapperWidth} border-primary text-primary ${className} border bg-white transition-all duration-200 rounded-xl px-2 ${padding_css} text-xs md:text-sm flex items-center justify-center gap-1 cursor-pointer`}
          >
            <span className="truncate">
              {hasCustomDate
                ? `${selectDate.startDate} → ${selectDate.endDate}`
                : isOpen
                ? "Select Date"
                : "All Records"}
            </span>

            <MdKeyboardArrowDown
              size={18}
              className={`shrink-0 opacity-60 transition-transform ${
                showOptionsMenu || isOpen
                  ? "rotate-180"
                  : ""
              }`}
            />
          </button>

          {showOptionsMenu && (
            <div
              className={`absolute left-0 z-50 mt-2 ${wrapperWidth} rounded-xl shadow-xl border overflow-hidden bg-white border-primary`}
            >
              <button
                type="button"
                onClick={handleSelectAllRecords}
                className={`block w-full px-3 py-2 text-left text-xs md:text-sm cursor-pointer text-primary ${
                  !hasCustomDate
                    ? "font-semibold"
                    : ""
                } hover:bg-stone-100`}
              >
                All Records
              </button>

              <button
                type="button"
                onClick={handleSelectDateOption}
                className={`block w-full px-3 py-2 text-left text-xs md:text-sm cursor-pointer border-t border-primary text-primary ${
                  hasCustomDate
                    ? "font-semibold"
                    : ""
                } hover:bg-stone-100`}
              >
                Select Date
              </button>
            </div>
          )}
        </>
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`${wrapperWidth} border-primary text-primary ${className} border bg-white transition-all duration-200 rounded-xl px-2 ${padding_css} text-xs md:text-sm`}
        >
          {selectDate?.date || "Select Date"}
        </button>
      )}

      {isOpen && (
        <div
          className={`absolute ${position} z-50 mt-2 flex flex-col sm:flex-row rounded-2xl shadow-2xl border overflow-hidden bg-white border-primary`}
        >
          <div className="w-full sm:w-40 p-2 border-b sm:border-b-0 sm:border-r space-y-1 border-primary bg-white">
            {presets.map((p, idx) => (
              <button
                key={idx}
                className="block w-full px-3 py-1 text-left text-sm rounded-lg cursor-pointer text-primary hover:bg-stone-100"
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col w-fit bg-white">
            <div className="overflow-x-auto w-fit">
              {mode === "range" ? (
                <DateRange
                  editableDateInputs
                  moveRangeOnFirstSelection={false}
                  onChange={(item) =>
                    setRange([
                      item.selection,
                    ])
                  }
                  ranges={range}
                  className="custom-tailwind-picker border border-primary rounded-xl shadow-sm"
                />
              ) : (
                <Calendar
                  date={singleDate}
                  onChange={
                    handleSingleDateChange
                  }
                  maxDate={today}
                  className="custom-tailwind-picker border border-primary rounded-xl shadow-sm"
                />
              )}
            </div>

            {mode === "range" && (
              <div className="flex justify-end gap-2 p-3">
                <button
                  className="px-3 py-1 text-sm rounded-md border cursor-pointer text-primary border-primary"
                  onClick={() =>
                    setIsOpen(false)
                  }
                >
                  Cancel
                </button>

                <button
                  className="px-3 py-1 text-xs rounded-md bg-blue-600 text-white cursor-pointer"
                  onClick={() => {
                    const {
                      startDate,
                      endDate,
                    } = range[0];

                    const formatted = {
                      startDate: format(
                        startDate,
                        "yyyy-MM-dd"
                      ),
                      endDate: format(
                        endDate,
                        "yyyy-MM-dd"
                      ),
                    };

                    setSelectDate(formatted);
                    onDateChange?.(formatted);

                    previousRangeRef.current = {
                      startDate,
                      endDate,
                    };

                    setIsOpen(false);
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePickerStyle;
